// DirectorMorningBrief — small, calm, mobile-first scan for admins.
//
// Three signals only:
//   1) Yesterday's incidents grouped by severity (link → /admin/incidents?dateFrom=Y&dateTo=Y)
//   2) Today's open billing balance (link → /admin/billing)
//   3) Unacknowledged-by-parent incidents (link → /admin/incidents?ack=pending)
//
// Implementation notes:
// - Pure read-only. Reuses RLS-scoped queries that already exist in the app.
// - Single round-trip per signal in parallel.
// - Render a compact card on /admin only (above the existing stat grid).
// - Friendly empty state when nothing needs attention ("All quiet — well done.").

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sun,
  Receipt,
  ShieldCheck,
  WarningCircle,
  ArrowRight,
} from "@phosphor-icons/react";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { centsToUsd } from "@/lib/billing";
import { fetchClearanceSummary, fetchRatioCompliance } from "@/lib/dailyOpsCompliance";

function yesterdayRange() {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const yyyy = yesterday.toISOString().slice(0, 10);
  return {
    label: yesterday.toLocaleDateString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
    }),
    iso: yyyy,
    from: `${yyyy}T00:00:00`,
    to: `${yyyy}T23:59:59`,
  };
}

export default function DirectorMorningBrief() {
  const [incYesterday, setIncYesterday] = useState(null); // {high, medium, low, total}
  const [openBalanceCents, setOpenBalanceCents] = useState(null);
  const [unackedCount, setUnackedCount] = useState(null);
  const [blockedCount, setBlockedCount] = useState(null);
  const [staffingRiskCount, setStaffingRiskCount] = useState(null);
  const [loading, setLoading] = useState(true);

  const y = yesterdayRange();

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const [incRes, invRes, unackRes, blockedRows, ratioRows] = await Promise.all([
          supabase
            .from("incidents")
            .select("id, severity")
            .gte("occurred_at", y.from)
            .lte("occurred_at", y.to),
          supabase
            .from("invoices")
            .select("amount_total_cents, status")
            .in("status", ["issued", "failed"]),
          supabase
            .from("incidents")
            .select("id", { count: "exact", head: true })
            .is("acknowledged_at", null),
          fetchClearanceSummary().catch(() => []),
          fetchRatioCompliance().catch(() => []),
        ]);
        if (!alive) return;
        const incs = incRes.data || [];
        const counts = { high: 0, medium: 0, low: 0, total: incs.length };
        incs.forEach((i) => {
          if (i.severity in counts) counts[i.severity] += 1;
        });
        setIncYesterday(counts);
        const open = (invRes.data || []).reduce(
          (s, r) => s + (r.amount_total_cents || 0),
          0,
        );
        setOpenBalanceCents(open);
        setUnackedCount(unackRes.count ?? 0);
        setBlockedCount((blockedRows || []).length);
        // Count classrooms with any breach (ratio OR cert) over the next 7 days.
        const todayIso = new Date().toISOString().slice(0, 10);
        const todaysBreaches = new Set();
        for (const r of ratioRows || []) {
          if (r.day === todayIso && (r.ratioBreach || r.certBreach)) {
            todaysBreaches.add(r.classroomId);
          }
        }
        setStaffingRiskCount(todaysBreaches.size);
      } catch {
        // Fail soft — widget hides itself.
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <section
        data-testid="admin-morning-brief-loading"
        className="rounded-2xl border border-neutral-200 bg-white p-5 text-sm text-neutral-500"
      >
        Loading morning brief…
      </section>
    );
  }

  if (incYesterday === null && openBalanceCents === null && unackedCount === null) {
    return null;
  }

  const allQuiet =
    (incYesterday?.total ?? 0) === 0 &&
    (openBalanceCents ?? 0) === 0 &&
    (unackedCount ?? 0) === 0 &&
    (blockedCount ?? 0) === 0 &&
    (staffingRiskCount ?? 0) === 0;

  return (
    <section
      data-testid="admin-morning-brief"
      data-quiet={allQuiet ? "true" : "false"}
      className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 via-white to-white p-5 sm:p-6 shadow-[0_2px_12px_rgba(20,22,26,0.04)]"
    >
      <header className="flex items-start gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
          <Sun size={20} weight="duotone" className="text-primary-hover" />
        </span>
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
            Morning brief · {y.label}
          </p>
          <h2 className="mt-0.5 font-heading text-lg font-semibold tracking-tight text-neutral-900">
            What needs your attention today
          </h2>
        </div>
      </header>

      {allQuiet ? (
        <p
          data-testid="admin-morning-brief-quiet"
          className="mt-4 rounded-2xl bg-primary/5 px-4 py-3 text-sm text-primary-hover"
        >
          All quiet — well done.
        </p>
      ) : (
        <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <BriefItem
            testId="admin-morning-brief-incidents"
            to={`/admin/incidents?dateFrom=${y.iso}&dateTo=${y.iso}`}
            icon={WarningCircle}
            tone={incYesterday?.high ? "warn" : "neutral"}
            label="Yesterday's incidents"
            primary={String(incYesterday?.total ?? 0)}
            sub={
              incYesterday?.total
                ? `${incYesterday.high} high · ${incYesterday.medium} med · ${incYesterday.low} low`
                : "No incidents reported"
            }
          />
          <BriefItem
            testId="admin-morning-brief-open-balance"
            to="/admin/billing"
            icon={Receipt}
            tone={(openBalanceCents ?? 0) > 0 ? "amber" : "neutral"}
            label="Open billing balance"
            primary={centsToUsd(openBalanceCents ?? 0)}
            sub={
              (openBalanceCents ?? 0) > 0
                ? "Across issued + failed invoices"
                : "No open balance"
            }
          />
          <BriefItem
            testId="admin-morning-brief-unacked"
            to="/admin/incidents"
            icon={ShieldCheck}
            tone={(unackedCount ?? 0) > 0 ? "warn" : "neutral"}
            label="Awaiting parent ack"
            primary={String(unackedCount ?? 0)}
            sub={
              (unackedCount ?? 0) === 1
                ? "1 report still unacknowledged"
                : `${unackedCount ?? 0} reports still unacknowledged`
            }
          />
          {(blockedCount ?? 0) > 0 && (
            <BriefItem
              testId="admin-morning-brief-blocked"
              to="/admin/compliance"
              icon={WarningCircle}
              tone="warn"
              label="Drop-off attention"
              primary={String(blockedCount)}
              sub={blockedCount === 1 ? "1 child has missing required records" : `${blockedCount} children with missing required records`}
            />
          )}
          {(staffingRiskCount ?? 0) > 0 && (
            <BriefItem
              testId="admin-morning-brief-staffing"
              to="/admin/compliance"
              icon={ShieldCheck}
              tone="amber"
              label="Staffing risk today"
              primary={String(staffingRiskCount)}
              sub={staffingRiskCount === 1 ? "1 classroom over-ratio or with cert gap" : `${staffingRiskCount} classrooms over-ratio or with cert gap`}
            />
          )}
        </ul>
      )}
    </section>
  );
}

function BriefItem({ testId, to, icon: Icon, tone, label, primary, sub }) {
  const toneCls =
    tone === "warn"
      ? "border-[#C6851E]/40 bg-[#C6851E]/5"
      : tone === "amber"
        ? "border-amber-300 bg-amber-50/60"
        : "border-neutral-200 bg-white";
  const iconCls =
    tone === "warn"
      ? "text-[#C6851E]"
      : tone === "amber"
        ? "text-amber-600"
        : "text-neutral-500";
  return (
    <li>
      <Link
        to={to}
        data-testid={testId}
        data-tone={tone}
        className={`group flex h-full items-start gap-3 rounded-2xl border p-3 transition-all hover:-translate-y-[1px] hover:shadow-sm ${toneCls}`}
      >
        <Icon size={18} weight="duotone" className={`mt-0.5 ${iconCls}`} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
            {label}
          </p>
          <p className="mt-0.5 font-heading text-xl font-semibold tracking-tight text-neutral-900">
            {primary}
          </p>
          <p className="mt-0.5 truncate text-[12px] text-neutral-600">{sub}</p>
        </div>
        <ArrowRight
          size={14}
          weight="bold"
          className="mt-1 text-neutral-300 transition group-hover:translate-x-0.5 group-hover:text-primary-hover"
          aria-hidden
        />
      </Link>
    </li>
  );
}
